import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, MapPin, Award, Users, Heart } from "lucide-react";
import { CountUp } from "@/components/CountUp";
import { COMPANY, HISTORY, STATS } from "@/lib/site-data";

export default function Rolunk() {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F8FAFC] to-white" />
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="grid lg:grid-cols-12 gap-10 items-end"
          >
            <div className="lg:col-span-7">
              <span className="eyebrow">Rólunk</span>
              <h1 className="mt-3 font-heading font-extrabold text-[#0B192C] text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.02]">
                Három évtizedes{" "}
                <span className="text-[#EA580C]">szakmai múlt</span>.
              </h1>
            </div>
            <div className="lg:col-span-5">
              <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
                A Fortuna Ponyva Kft. 2003 óta gyárt és javít ipari minőségű
                ponyvákat. Több ezer ügyfél, több ezer egyedi munka mögöttünk –
                minden öltésünkben az a tudás, amit csak évtizedek alatt lehet
                megszerezni.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-[#0B192C] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 18px)" }} />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 relative">
          {STATS.map((s) => {
            const m = s.value.match(/^([\d.]+)(.*)$/);
            return (
              <div key={s.label} className="text-center md:text-left">
                <div className="font-heading font-extrabold text-[#EA580C] text-3xl md:text-5xl">
                  {m ? <CountUp end={parseFloat(m[1])} suffix={m[2]} /> : s.value}
                </div>
                <div className="text-xs md:text-sm text-gray-400 mt-1 uppercase tracking-widest">
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Timeline - rare design element */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <span className="eyebrow">Fejlődésünk</span>
          <h2 className="mt-3 font-heading font-bold text-[#0B192C] text-3xl sm:text-4xl tracking-tight max-w-2xl">
            Egy időszak, egy mérföldkő.
          </h2>

          <div className="mt-12 relative">
            {/* vertical timeline line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#EA580C] via-gray-200 to-[#1E3E62]" />

            <div className="space-y-10 md:space-y-16">
              {HISTORY.map((m, i) => {
                const left = i % 2 === 0;
                return (
                  <motion.div
                    key={m.year}
                    initial={{ opacity: 0, x: left ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6, delay: i * 0.08 }}
                    className={`relative grid md:grid-cols-2 gap-6 ${
                      left ? "" : "md:[direction:rtl]"
                    }`}
                  >
                    {/* node */}
                    <div className="absolute left-4 md:left-1/2 top-3 w-3 h-3 -translate-x-1/2 rounded-full bg-[#EA580C] ring-4 ring-white shadow z-10" />

                    <div className={`pl-12 md:pl-0 ${left ? "md:pr-12 md:text-right" : "md:pl-12 [direction:ltr]"}`}>
                      <div className="font-heading font-extrabold text-[#EA580C] text-3xl md:text-4xl">
                        {m.year}
                      </div>
                      <h3 className="mt-1 font-heading font-bold text-[#0B192C] text-xl">
                        {m.title}
                      </h3>
                      <p className="mt-2 text-gray-600 text-sm leading-relaxed">
                        {m.text}
                      </p>
                    </div>
                    <div />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F8FAFC] py-16 md:py-24 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <span className="eyebrow">Értékeink</span>
          <h2 className="mt-3 font-heading font-bold text-[#0B192C] text-3xl sm:text-4xl tracking-tight max-w-2xl">
            Amit minden munkánkba beleadunk.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              { icon: Award, title: "Minőség" },
              { icon: Users, title: "Megbízhatóság" },
              { icon: Heart, title: "Rugalmasság" },
            ].map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white p-7 rounded-lg border border-gray-200 hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 rounded-md bg-[#EA580C] text-white flex items-center justify-center">
                  <v.icon className="w-6 h-6" />
                </div>
                <h3 className="mt-5 font-heading font-bold text-[#0B192C] text-xl">
                  {v.title}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="eyebrow">Hol találja meg műhelyünket?</span>
            <h2 className="mt-3 font-heading font-bold text-[#0B192C] text-3xl sm:text-4xl tracking-tight">
              Dunaharaszti, az M0 mellett.
            </h2>
            <p className="mt-5 text-gray-600 leading-relaxed">
              Telephelyünk Budapest dél-pesti határától néhány perc távolságra
              található. Könnyen megközelíthető az M0 és M5 autópályáról is – minden méretű járművel be tud állni.
            </p>
            <div className="mt-6 flex items-center gap-3 text-[#0B192C]">
              <MapPin className="w-5 h-5 text-[#EA580C]" />
              <span className="font-semibold">{COMPANY.address}</span>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-[#EA580C] hover:bg-[#C2410C] h-12 px-6 font-semibold">
                <Link to="/#ajanlat">Ajánlatkérés <ArrowRight className="w-4 h-4" /></Link>
              </Button>
              <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border-2 border-[#1E3E62] text-[#1E3E62] hover:bg-[#1E3E62] hover:text-white font-semibold transition-colors">
                <Phone className="w-4 h-4" />{COMPANY.phone}
              </a>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-lg overflow-hidden border border-gray-200 shadow-md aspect-[4/3]">
              <iframe
                title="Fortuna Ponyva – Dunaharaszti"
                src={COMPANY.mapEmbed}
                width="100%"
                height="100%"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{ border: 0 }}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
